import React, { useEffect, useState, useRef } from "react";// v1.25.0 — Multi-tenant dashboard header shows the LOGGED-IN COMPANY name.
import { useNavigate, useLocation } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { dmY, dmYhm } from "@/lib/dateFmt";
import { sortPaymentModes } from "@/lib/paymentModes";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import Brand from "@/components/Brand";
import ConfettiBurst from "@/components/ConfettiBurst";
import SparklerFireworks, { isNationalDayToday } from "@/components/SparklerFireworks";
import CountUp from "@/components/CountUp";
import InboxZeroOverlay from "@/components/InboxZeroOverlay";
import MonthlySlipSummary from "@/components/MonthlySlipSummary";
import { TriColorWavingFlag, isTriColorSeason } from "@/components/TriColorRibbon";
import FestivalBanner from "@/components/FestivalBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import DailyBroadcastCard from "@/components/DailyBroadcastCard";
import { CurrencyInr, UsersThree, UploadSimple, CheckCircle, PaperPlaneTilt, Plus, Trash, ArrowRight, ChartLineUp, Warning, Trophy, Broom, Table as TableIcon, Flame, SpeakerHigh, SpeakerSlash, Scales } from "@phosphor-icons/react";
import { isSoundMuted, setSoundMuted } from "@/lib/approveSound";
import AmountInput from "@/components/AmountInput";
import CashDenominationCounter from "@/components/CashDenominationCounter";
import TopPartiesWidget from "@/components/TopPartiesWidget";
import CashInHandTile from "@/components/CashInHandTile";

// v1.28.67 — Listen for global keyboard-shortcut events so Ctrl+P / Ctrl+O
// open the Cash / Payout dialogs from anywhere in the app.
// v1.29.34 — Cross-form payment-mode direction guard. A mode whose name
// contains the word RECD/RECEIVED belongs on the Value Received (Amt Recd)
// form; a mode containing PAID/OUT belongs on the Amt Paid / Value Out form.
// Word-boundary + case-insensitive: "Cash Paid" matches, "Outstation" doesn't.
const RECD_MODE_RE = /\b(RECD|RECEIVED)\b/i;
const PAID_MODE_RE = /\b(PAID|OUT)\b/i;

function useShortcutDialogs(setCashOpen, setPayoutOpen, loadModes) {
    React.useEffect(() => {
        const onSc = (e) => {
            const a = e?.detail?.action_id;
            if (a === "record-payment") { setCashOpen(true); loadModes?.(); }
            else if (a === "record-payout") { setPayoutOpen(true); loadModes?.(); }
        };
        window.addEventListener("micm-shortcut", onSc);
        return () => window.removeEventListener("micm-shortcut", onSc);
    }, [setCashOpen, setPayoutOpen, loadModes]);
}

const fmt = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);
const fmt2 = (n) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(n || 0);
// Indian short-form: 1,00,00,000 → ₹1 Cr · 1,00,000 → ₹1 L · 1,000 → ₹1 K
const fmtCompact = (n) => {
    const v = Number(n) || 0;
    const abs = Math.abs(v);
    const sign = v < 0 ? "-" : "";
    if (abs >= 10000000) return `${sign}₹${(v / 10000000).toFixed(v % 10000000 === 0 ? 0 : 2).replace(/\.00$/, "")} Cr`;
    if (abs >= 100000) return `${sign}₹${(v / 100000).toFixed(v % 100000 === 0 ? 0 : 2).replace(/\.00$/, "")} L`;
    if (abs >= 1000) return `${sign}₹${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1).replace(/\.0$/, "")} K`;
    return fmt(v);
};

// v1.24.12 — Record Payment dialog: pick party ONCE, then add many entries
// (Date · Amount · Payment Mode · Remarks). Payment modes come from the DB
// via /payment-modes so newly-added ones stick and are reusable.
const blankLine = (defaultMode = "") => ({
    id: crypto.randomUUID(),
    date: new Date().toISOString().slice(0, 10),
    amount: "",
    payment_mode: defaultMode,
    remarks: "",
});

export default function AdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [parties, setParties] = useState([]);
    const [payoutOpen, setPayoutOpen] = useState(false);
    const [payoutPartyId, setPayoutPartyId] = useState("");   // v1.24.12 — one party per batch
    const [payoutDate, setPayoutDate] = useState(new Date().toISOString().slice(0, 10));
    // v1.25.0 — logged-in tenant name for the welcome banner
    const [company, setCompany] = useState(null);
    // v1.25.2 — advance alerts panel toggled from the tile below
    const [showAdvancePanel, setShowAdvancePanel] = useState(false);
    useEffect(() => {
        api.get("/me/company").then((r) => setCompany(r.data)).catch(() => setCompany(null));
    }, []);
    const [lines, setLines] = useState([blankLine()]);
    const [busy, setBusy] = useState(false);
    const [payoutError, setPayoutError] = useState("");
    const [burst, setBurst] = useState(false);   // v1.25.46 — payout confetti
    const [fireworks, setFireworks] = useState(false);  // v1.27.15 — national day sparklers
    const [inboxZero, setInboxZero] = useState(false); // v1.25.48 — inbox-zero celebration
    const [muted, setMuted] = useState(isSoundMuted()); // v1.25.49 — sound toggle
    const prevPendingRef = React.useRef(null);
    const [outstandingOpen, setOutstandingOpen] = useState(false);
    const [outstandingRows, setOutstandingRows] = useState(null);
    const [paymentModes, setPaymentModes] = useState([]);
    // v1.29.20 — Shared sort+format helper standardises A-Z (case-insensitive)
    // + Title-Case display + fixes the "RECIVED" typo across every render
    // site on this page (Payout, Cash-In, Payment Modes managers). Any new
    // mode POSTed to /payment-modes slots into place automatically on the
    // next render — no per-site sort call needed.
    const sortedPaymentModes = React.useMemo(
        () => sortPaymentModes(paymentModes),
        [paymentModes],
    );
    const [newModeName, setNewModeName] = useState("");
    const [cashOpen, setCashOpen] = useState(false);
    const [cashLines, setCashLines] = useState([{ id: crypto.randomUUID(), party_id: "", amount: "", charge_percent: "", remarks: "", payment_mode: "Cash" }]);
    const [cashDate, setCashDate] = useState(new Date().toISOString().slice(0, 10));
    const [cashBusy, setCashBusy] = useState(false);
    const [cashError, setCashError] = useState("");
    // Cache of party.id → their pricing-rule % (loaded lazily as parties are selected).
    // Shown as a "Party default: X%" hint under the Charge % field so the user
    // can optionally *apply* it. We deliberately do NOT auto-populate — the
    // safe default is "no commission" until the user explicitly opts in.
    const [partyRulePct, setPartyRulePct] = useState({});
    // v1.25.76 — Cache of party.id → current outstanding balance so the
    // Record Payments header can display it next to the selected party name.
    const [partyBalances, setPartyBalances] = useState({});

    // v1.26.0 — Duplicate-payment guard state. Holds the pending payload +
    // conflict details when the backend returns 409 DUPLICATE_PAYMENT so the
    // admin can either Post Anyway (retries with override_duplicate=true)
    // or Reject the entry.
    const [dupWarning, setDupWarning] = useState(null);
    const [dupBusy, setDupBusy] = useState(false);

    // Deep-link support for the Quick-Actions FAB: /admin?open=cash|payout
    // opens the matching modal automatically. The URL param is then cleared
    // so a refresh doesn't re-open the dialog.
    // v1.20.11 — also captures `back=<url>` so Save can return the admin
    // to where they were (e.g. the Party Ledger reconcile view).
    // v1.28.53 — Read `back` from window.location SYNCHRONOUSLY at first
    // render AND keep it in a ref so async React state updates or the
    // "clear query params on mount" navigate() can never wipe it before
    // the save handler fires. Fixes reports of "after saving a payment on
    // CITY LEATHER ledger, app returns to main /admin instead of the ledger".
    const location = useLocation();
    const backUrlRef = useRef(
        (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("back")) || null
    );
    const [backUrl, setBackUrl] = useState(backUrlRef.current);
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const which = params.get("open");
        const back = params.get("back");
        if (back) { backUrlRef.current = back; setBackUrl(back); }
        if (which === "cash") { setCashOpen(true); loadModes(); navigate("/admin", { replace: true }); }
        else if (which === "payout") { setPayoutOpen(true); loadModes(); navigate("/admin", { replace: true }); }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // v1.27.16 — On Independence Day (15-Aug) or Republic Day (26-Jan): fire
    // sparklers immediately when the dashboard opens, then EVERY hour after
    // that while the dashboard remains mounted. If the user reopens the app,
    // the initial fire happens again (fresh mount). No localStorage dedupe —
    // user explicitly asked for hourly celebration through the day.
    useEffect(() => {
        if (!isNationalDayToday()) return;
        const initial = setTimeout(() => setFireworks(true), 800);
        const hourly = setInterval(() => setFireworks(true), 60 * 60 * 1000);
        return () => { clearTimeout(initial); clearInterval(hourly); };
    }, []);

    // v1.27.74 — Add explicit "Record Payment / Payout" buttons ON the ledger
    // page so admin never has to leave. Same dialog UX as Dashboard, but the
    // save handler simply calls `reloadLedger()` — no navigation. Party is
    // pre-selected to the currently-viewed party; date defaults to today.
    // Helper: after a successful quick-action save, either return the admin
    // to the referring page (Party Ledger, Suspense, etc.) or stay on Dashboard.
    const returnToSourceIfAny = () => {
        // v1.28.53 — Prefer the ref (never overwritten by React re-renders /
        // URL param clearing) then fall back to state. Guarantees the admin
        // always lands back on the ledger they came from.
        const target = backUrlRef.current || backUrl;
        if (target) {
            try {
                const decoded = decodeURIComponent(target);
                navigate(decoded, { replace: true });
                // Fire a global event so a still-mounted ledger component
                // knows to refetch even when URL params are unchanged.
                window.dispatchEvent(new CustomEvent("micm-ledger-refresh"));
            } catch { navigate(-1); }
            backUrlRef.current = null;
            setBackUrl(null);
        }
    };

    const validCashLines = cashLines.filter((l) => l.party_id && Number(l.amount) > 0);
    const cashTotal = validCashLines.reduce((s, l) => s + Number(l.amount || 0), 0);
    // v1.29.34 — a PAID/OUT mode selected on the Value In form blocks save.
    const paidModeOnCash = cashLines.some((l) => PAID_MODE_RE.test(l.payment_mode || ""));
    const cashCommissionTotal = validCashLines.reduce((s, l) => {
        const pct = Number(l.charge_percent || 0);
        return pct > 0 ? s + (Number(l.amount || 0) * pct / 100) : s;
    }, 0);

    const setCashLineField = (id, field, value) => {
        setCashLines((prev) => prev.map((x) => {
            if (x.id !== id) return x;
            // v1.27.57 — Auto-fill Remark from the mode's preset on
            // payment_mode change, mirroring the payout dialog behaviour.
            if (field === "payment_mode" && value !== x.payment_mode) {
                const { newRemarks, newPresetSnapshot } = _applyModeChangeToLine(x, value, cashRemarkPreset);
                setCashRemarkPreset((p) => ({ ...p, [id]: newPresetSnapshot }));
                return { ...x, payment_mode: value, remarks: newRemarks };
            }
            return { ...x, [field]: value };
        }));
    };

    const onCashPartySelected = async (line_id, party_id) => {
        // Set party immediately WITHOUT touching charge_percent — the user
        // decides whether to apply commission for this specific receipt.
        setCashLineField(line_id, "party_id", party_id);
        if (!partyRulePct.hasOwnProperty(party_id)) {
            try {
                const { data } = await api.get("/pricing", { params: { party_id } });
                const rule = Array.isArray(data) ? data.find((r) => Number(r.charge_percent) > 0) : null;
                const pct = rule ? Number(rule.charge_percent) : 0;
                setPartyRulePct((prev) => ({ ...prev, [party_id]: pct }));
            } catch (_e) {
                setPartyRulePct((prev) => ({ ...prev, [party_id]: 0 }));
            }
        }
    };

    const submitCash = async (e) => {
        e.preventDefault();
        if (validCashLines.length === 0) { toast.error("Add at least one party + amount"); return; }
        // v1.29.34 — belt-and-braces: also block Enter-key form submits (button disable alone doesn't stop those)
        if (paidModeOnCash) { toast.error("A selected payment mode is for Value Paid / Amt Out — change it before saving."); return; }
        // Extra guard: any line with a %-value but zero amount, or negative %, is rejected client-side.
        for (const l of validCashLines) {
            const pct = Number(l.charge_percent || 0);
            if (pct < 0 || pct > 100) {
                toast.error("Charge % must be between 0 and 100");
                return;
            }
        }
        await runCashSubmit(false);
    };

    // v1.26.0 — Actual API-caller for Record Cash Received. Extracted so we can
    // call it again after the admin clicks "Post Anyway" on the duplicate dialog.
    const runCashSubmit = async (overrideDuplicate) => {
        // v1.29.41 — crash guard: log start/end, surface failures inline,
        // never let a 401 bounce the app to /login mid-submit.
        console.log("[payment-submit] cash start", { lines: validCashLines.length, overrideDuplicate });
        setCashError("");
        setCashBusy(true);
        try {
            let firstDupHit = null;
            const remaining = [];
            for (const l of validCashLines) {
                const pct = Number(l.charge_percent || 0);
                try {
                    await api.post("/payments/cash", {
                        party_id: l.party_id,
                        date: cashDate,
                        amount: Number(l.amount),
                        charge_percent: pct > 0 ? pct : 0,
                        remarks: l.remarks || "",
                        payment_mode: l.payment_mode || "Cash",
                        override_duplicate: overrideDuplicate,
                    }, { skipAuthRedirect: true });
                } catch (err) {
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    if (status === 409 && detail?.code === "DUPLICATE_PAYMENT") {
                        firstDupHit = { line: l, detail };
                        remaining.push(l);
                        // stop the loop on first duplicate so admin sees the exact conflict
                        break;
                    }
                    throw err;
                }
            }
            if (firstDupHit) {
                setDupWarning({
                    kind: "cash",
                    line: firstDupHit.line,
                    message: firstDupHit.detail.message,
                    existing: firstDupHit.detail.existing,
                    // pendingLines is set so "Post Anyway" retries only the remaining lines
                    pendingLines: remaining,
                });
                return;
            }
            const withCommission = validCashLines.filter((l) => Number(l.charge_percent || 0) > 0).length;
            toast.success(
                `${validCashLines.length} payment${validCashLines.length > 1 ? "s" : ""} recorded · total ${fmt2(cashTotal)}${
                    withCommission > 0
                        ? ` · commission on ${withCommission} of ${validCashLines.length} (₹${fmt2(cashCommissionTotal).replace(/^₹/, "")})`
                        : " · no commission applied"
                }`,
            );
            console.log("[payment-submit] cash success");
            setCashOpen(false);
            setCashLines([{ id: crypto.randomUUID(), party_id: "", amount: "", charge_percent: "", remarks: "", payment_mode: "Cash" }]);
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
            returnToSourceIfAny();
        } catch (e) { console.error("[payment-submit] cash failed", e?.response?.status, e); setCashError(formatApiError(e)); toast.error(formatApiError(e)); }
        finally { setCashBusy(false); }
    };

    const loadModes = () => {
        api.get("/payment-modes").then((r) => {
            const list = Array.isArray(r.data) ? r.data : [];
            // v1.20.11 — always guarantee a usable dropdown. If the DB has no
            // payment modes yet, seed common defaults locally so the Record
            // Payments dialog is never blank.
            if (list.length === 0) {
                setPaymentModes([
                    { id: "_seed_cash", name: "Cash", usage_count: 0 },
                    { id: "_seed_bank", name: "Bank Transfer", usage_count: 0 },
                    { id: "_seed_upi", name: "UPI", usage_count: 0 },
                    { id: "_seed_cheque", name: "Cheque", usage_count: 0 },
                    { id: "_seed_other", name: "Other", usage_count: 0 },
                ]);
            } else {
                setPaymentModes(list);
            }
        }).catch(() => {
            setPaymentModes([
                { id: "_seed_cash", name: "Cash", usage_count: 0 },
                { id: "_seed_bank", name: "Bank Transfer", usage_count: 0 },
                { id: "_seed_upi", name: "UPI", usage_count: 0 },
                { id: "_seed_cheque", name: "Cheque", usage_count: 0 },
                { id: "_seed_other", name: "Other", usage_count: 0 },
            ]);
        });
    };

    const addMode = async () => {
        const name = newModeName.trim();
        if (!name) return;
        try {
            const { data } = await api.post("/payment-modes", { name });
            setPaymentModes((prev) => {
                const exists = prev.some((m) => (m.name || "").toLowerCase() === name.toLowerCase());
                return exists ? prev : [{ ...data, usage_count: 0 }, ...prev];
            });
            setNewModeName("");
            toast.success(`Added "${data.name || name}"`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openOutstanding = async () => {
        setOutstandingOpen(true);
        setOutstandingRows(null);
        try {
            const { data } = await api.get("/dashboard/outstanding-by-party");
            setOutstandingRows(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const load = () => {
        api.get("/dashboard/admin").then((r) => setData(r.data)).catch(() => {});
        api.get("/parties").then((r) => setParties(Array.isArray(r.data) ? r.data : [])).catch((e) => {
            toast.error(`Could not load parties · ${formatApiError(e)}`);
        });
    };

    const openCashDialog = () => {
        setCashLines([{ id: crypto.randomUUID(), party_id: "", amount: "", remarks: "", payment_mode: "Cash" }]);
        setCashDate(new Date().toISOString().slice(0, 10));
        setCashOpen(true);
        loadModes();  // v1.27.24 — Cash-In dialog was missing this fetch so
                      // Payment Mode dropdown showed empty despite modes existing.
        // Force refresh parties on open — guarantees the dropdown has latest data
        api.get("/parties").then((r) => setParties(Array.isArray(r.data) ? r.data : [])).catch((e) => {
            toast.error(`Could not load parties · ${formatApiError(e)}`);
        });
    };

    // Hide inactive parties from the dashboard dropdowns entirely
    // (they still show on the Parties management page).
    const sortedParties = parties
        .filter((p) => p.active !== false)
        .slice()
        .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

    useEffect(() => {
        load();
        const interval = setInterval(load, 20000);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    const openPayoutDialog = () => {
        setLines([blankLine()]);
        setPayoutPartyId("");
        setPayoutDate(new Date().toISOString().slice(0, 10));
        setPayoutOpen(true);
    };

    // v1.25.77 — Fetch party balances whenever the payout dialog opens.
    // Previously the fetch lived inside openPayoutDialog(), so opening the
    // dialog via any other route (deep-link ?open=payout, FAB, nav link)
    // skipped it entirely and the balance chip never appeared. This effect
    // covers every open path.
    useEffect(() => {
        if (!payoutOpen) return;
        api.get("/parties/balances")
            .then((r) => setPartyBalances(r.data || {}))
            .catch(() => { /* silent — chip just won't render */ });
    }, [payoutOpen]);

    // v1.27.57 — Track the Remark Preset that was auto-filled per line so
    // when the admin flips the Payment Mode again we can safely swap the
    // preset without stomping on a manual edit. Key: line.id → last preset
    // that we auto-filled into it (or "" if we never touched the field).
    const [payoutRemarkPreset, setPayoutRemarkPreset] = useState({});
    const [cashRemarkPreset, setCashRemarkPreset] = useState({});

    const _presetForMode = (modeName) => {
        const m = paymentModes.find((x) => (x.name || "").toLowerCase() === (modeName || "").toLowerCase());
        return (m?.default_remark || "").trim();
    };

    const _applyModeChangeToLine = (line, newMode, presetMap) => {
        const preset = _presetForMode(newMode);
        const currentRemark = line.remarks || "";
        const previousAutoFill = presetMap[line.id] || "";
        // Only overwrite the Remark when the current value is empty OR still
        // equals the previous auto-filled preset. This preserves any manual
        // edit the admin already typed.
        const shouldOverwrite = currentRemark === "" || currentRemark === previousAutoFill;
        return {
            newRemarks: shouldOverwrite ? preset : currentRemark,
            newPresetSnapshot: shouldOverwrite ? preset : previousAutoFill,
        };
    };

    const updateLine = (id, patch) => setLines((prev) => prev.map((l) => {
        if (l.id !== id) return l;
        if (patch.payment_mode && patch.payment_mode !== l.payment_mode) {
            const { newRemarks, newPresetSnapshot } = _applyModeChangeToLine(l, patch.payment_mode, payoutRemarkPreset);
            setPayoutRemarkPreset((p) => ({ ...p, [id]: newPresetSnapshot }));
            return { ...l, ...patch, remarks: newRemarks };
        }
        return { ...l, ...patch };
    }));
    const addLine = () => setLines((prev) => [...prev, blankLine(paymentModes[0]?.name || "")]);
    const removeLine = (id) => setLines((prev) => (prev.length > 1 ? prev.filter((l) => l.id !== id) : prev));

    const validLines = lines.filter((l) => l.date && Number(l.amount) > 0);
    const total = validLines.reduce((s, l) => s + Number(l.amount || 0), 0);
    // v1.29.34 — a RECD/RECEIVED mode selected on the Value Out form blocks save.
    const recdModeOnPayout = lines.some((l) => RECD_MODE_RE.test(l.payment_mode || ""));

    const submitPayouts = async (e) => {
        e.preventDefault();
        if (!payoutPartyId) {
            toast.error("Choose the party first");
            return;
        }
        // v1.29.34 — also block Enter-key form submits when a RECD/RECEIVED mode is selected
        if (recdModeOnPayout) { toast.error("A selected payment mode is for Value Received — change it before saving."); return; }
        if (validLines.length === 0) {
            toast.error("Add at least one entry with a date and amount");
            return;
        }
        await runPayoutSubmit(false);
    };

    // v1.26.0 — Actual API-caller for bulk payouts. Extracted so the
    // "Post Anyway" button on the duplicate dialog can retry with the
    // override_duplicate flag flipped on.
    const runPayoutSubmit = async (overrideDuplicate) => {
        console.log("[payment-submit] payout start", { lines: validLines.length, overrideDuplicate });
        setPayoutError("");
        setBusy(true);
        try {
            const sorted = [...validLines].sort((a, b) => a.date.localeCompare(b.date));
            const body = {
                date: payoutDate,
                party_id: payoutPartyId,
                lines: sorted.map((l) => ({
                    amount: Number(l.amount),
                    date: l.date,
                    payment_mode: l.payment_mode || "Cash Paid",
                    notes: l.remarks || "",
                })),
                override_duplicate: overrideDuplicate,
            };
            let data;
            try {
                const resp = await api.post("/payouts/bulk", body, { skipAuthRedirect: true });
                data = resp.data;
            } catch (err) {
                const status = err?.response?.status;
                const detail = err?.response?.data?.detail;
                if (status === 409 && detail?.code === "DUPLICATE_PAYMENT") {
                    setDupWarning({
                        kind: "payout",
                        message: detail.message,
                        duplicates: detail.duplicates || [],
                        pendingBody: body,
                    });
                    return;
                }
                throw err;
            }
            const partyName = sortedParties.find((p) => p.id === payoutPartyId)?.name || "party";
            toast.success(`${data.count} payment${data.count > 1 ? "s" : ""} recorded to ${partyName} — total ${fmt2(data.total)}`);
            console.log("[payment-submit] payout success");
            setPayoutOpen(false);
            setLines([blankLine()]);
            setPayoutPartyId("");
            setBurst(true);
            load();
            window.dispatchEvent(new CustomEvent("micm-cash-updated"));
            returnToSourceIfAny();
        } catch (e) { console.error("[payment-submit] payout failed", e?.response?.status, e); setPayoutError(formatApiError(e)); toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };

    // v1.28.67 — Ctrl+P / Ctrl+O opens the Cash / Payout dialogs
    useShortcutDialogs(setCashOpen, setPayoutOpen, loadModes);

    // v1.25.48 — Watch pending_uploads → if it transitions from >0 to 0, celebrate!
    useEffect(() => {
        const now = Number(data?.pending_uploads);
        if (Number.isFinite(now)) {
            const prev = prevPendingRef.current;
            if (prev != null && prev > 0 && now === 0) {
                setInboxZero(true);
            }
            prevPendingRef.current = now;
        }
    }, [data?.pending_uploads]);

    const [medComm, setMedComm] = useState(null);
    useEffect(() => { api.get("/dashboard/mediator-commission").then((r) => setMedComm(r.data)).catch(() => {}); }, []);
    const [portalBal, setPortalBal] = useState(null);
    const [portalFrom, setPortalFrom] = useState("");
    const [portalTo, setPortalTo] = useState("");
    useEffect(() => {
        const qs = [];
        if (portalFrom) qs.push(`date_from=${portalFrom}`);
        if (portalTo) qs.push(`date_to=${portalTo}`);
        api.get(`/dashboard/portal-balances${qs.length ? "?" + qs.join("&") : ""}`).then((r) => setPortalBal(r.data)).catch(() => {});
    }, [portalFrom, portalTo]);

    // Task 2 — "Pending Uploads" tile now shows today's uploads + pending total.
    // Task 5 — 4th tile replaced with "Commission · last 3 months" (big number + sparkline).
    const stats = [
        {
            label: "Total Outstanding",
            value: fmtCompact(data?.total_outstanding),
            raw: Number(data?.total_outstanding || 0),
            format: fmtCompact,
            sub: fmt(data?.total_outstanding || 0),
            icon: CurrencyInr,
            accent: "from-rose-500 to-rose-600",
            tone: "text-slate-900",
            onClick: openOutstanding,
            hint: "Party-wise breakdown",
        },
        {
            label: "Verified · This Month",
            value: fmtCompact(data?.verified_this_month),
            raw: Number(data?.verified_this_month || 0),
            format: fmtCompact,
            sub: fmt(data?.verified_this_month || 0),
            icon: CheckCircle,
            accent: "from-emerald-500 to-emerald-600",
            tone: "text-emerald-700",
            onClick: () => navigate("/admin/uploads?status=verified"),
            hint: "View verified slips",
        },
    ];

    return (
        <div data-testid="admin-dashboard" className="space-y-4">
            <ConfettiBurst open={burst} onClose={() => setBurst(false)} />
            <SparklerFireworks open={fireworks} onClose={() => setFireworks(false)} />
            <InboxZeroOverlay open={inboxZero} onClose={() => setInboxZero(false)} />
            {/* v1.27.95 — Auto Festival Banner. Visible only within
                [festival − 4 days, festival + 1 day] window. Contains no
                data / tables — sits above the welcome header and disappears
                the day after the festival with zero admin action. Preview
                any theme with `?festival=<key>` in the URL. */}
            <FestivalBanner />
            {/* v1.27.84 — Welcome header now sits BEFORE any tile / broadcast /
                summary card on every admin screen so the user always sees the
                "Dashboard" / company banner first, then the actionable tiles. */}
            {/* v1.27.71 — Rebuilt admin welcome header so nothing overlaps on
                mobile. Vertical stack (top → bottom):
                  1. Slim tri-color ribbon (only during 13-16 Aug / 24-27 Jan)
                  2. Caption ("Dashboard" or festive)
                  3. "Welcome to <Company>" heading
                  4. Chip strip: Inbox-Zero streak + FIRE (season only) + sound toggle
                  5. "Collections · Uploads · Party balances" caption
                  6. Slim tri-color ribbon (season only)
                Previously the FIRE badge, Inbox-Zero pill and sound button were
                absolute-positioned on top of the flag bands, which collided
                with the middle band's Welcome text on narrow viewports. */}
            <div
                className={`relative overflow-hidden rounded-xl border border-slate-200 shadow-sm ${isTriColorSeason() ? "bg-white" : ""}`}
                data-testid={isTriColorSeason() ? "admin-welcome-flag" : "admin-welcome-card"}
            >
                {isTriColorSeason() && (
                    <div className="flex h-2 w-full" aria-hidden data-testid="admin-welcome-flag-ribbon-top">
                        <div className="flex-1 bg-[#FF9933]" />
                        <div className="flex-1 bg-white" />
                        <div className="flex-1 bg-[#138808]" />
                    </div>
                )}
                <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-2.5 ${isTriColorSeason() ? "" : "bg-gradient-to-r from-teal-700 to-emerald-700"}`}>
                    <h1
                        data-testid="admin-welcome"
                        className={`font-display text-base font-bold tracking-tight sm:text-lg ${isTriColorSeason() ? "text-slate-900" : "text-white"}`}
                    >
                        {company?.name || "Your company"}
                    </h1>
                    {data?.inbox_zero_streak_days != null && (
                        <div
                            data-testid="inbox-zero-streak"
                            title={data.inbox_zero_streak_days === 0 ? "You just cleared the queue — streak starts now!" : `${data.inbox_zero_streak_days} day${data.inbox_zero_streak_days === 1 ? "" : "s"} at Inbox Zero`}
                            className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 ${
                                isTriColorSeason()
                                    ? "border border-amber-300 bg-amber-50"
                                    : "border border-amber-300/60 bg-amber-400/20 backdrop-blur-sm"
                            }`}
                        >
                            <Flame size={13} weight="fill" className={isTriColorSeason() ? "text-amber-600 animate-pulse" : "text-amber-300 animate-pulse"} />
                            <span className={`text-[11px] font-bold ${isTriColorSeason() ? "text-amber-900" : "text-white"}`}>
                                <CountUp to={data.inbox_zero_streak_days} format={(n) => Math.round(n).toString()} /> day{data.inbox_zero_streak_days === 1 ? "" : "s"} streak
                            </span>
                        </div>
                    )}
                    {isTriColorSeason() && (
                        <button
                            type="button"
                            data-testid="fire-sparklers-btn"
                            onClick={() => setFireworks(true)}
                            title="Tap for a fireworks burst"
                            className="flex h-7 items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 px-2.5 text-amber-900 transition-transform hover:scale-105 hover:bg-amber-100"
                        >
                            <span className="text-sm leading-none">🎆</span>
                            <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Fire!</span>
                        </button>
                    )}
                    <button
                        type="button"
                        data-testid="sound-toggle"
                        onClick={() => { const v = !muted; setMuted(v); setSoundMuted(v); }}
                        title={muted ? "Sound OFF — click to enable approval chime" : "Sound ON — click to mute approval chime"}
                        className={`ml-auto flex h-7 w-7 shrink-0 items-center justify-center rounded-full transition-colors ${
                            isTriColorSeason()
                                ? "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                                : "border border-white/30 bg-white/10 text-white hover:bg-white/20"
                        }`}
                    >
                        {muted ? <SpeakerSlash size={13} weight="bold" /> : <SpeakerHigh size={13} weight="bold" />}
                    </button>
                </div>
                {isTriColorSeason() && (
                    <div className="flex h-2 w-full" aria-hidden data-testid="admin-welcome-flag-ribbon-bottom">
                        <div className="flex-1 bg-[#FF9933]" />
                        <div className="flex-1 bg-white" />
                        <div className="flex-1 bg-[#138808]" />
                    </div>
                )}
            </div>
            {/* v1.27.84 — Broadcast + Monthly Slip Summary tiles now render
                AFTER the welcome header so the header is always the first
                block on every admin screen. */}
            <DailyBroadcastCard />
            {/* v1.31.3 — Portal Balances: verified uploads − mediator withdrawals per portal. Always visible to Admin. */}
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm" data-testid="portal-balances-section">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="font-display text-lg font-bold text-slate-900">Portal Balances</h2>
                    <div className="flex items-center gap-2">
                        <input type="date" data-testid="portal-balance-from" value={portalFrom} onChange={(e) => setPortalFrom(e.target.value)} className="h-7 rounded-md border border-slate-200 px-1.5 text-[11px]" />
                        <span className="text-[11px] text-slate-400">→</span>
                        <input type="date" data-testid="portal-balance-to" value={portalTo} onChange={(e) => setPortalTo(e.target.value)} className="h-7 rounded-md border border-slate-200 px-1.5 text-[11px]" />
                        {(portalFrom || portalTo) && <button onClick={() => { setPortalFrom(""); setPortalTo(""); }} data-testid="portal-balance-clear" className="text-[11px] font-semibold text-rose-600 hover:underline">Clear</button>}
                        <button onClick={() => navigate("/admin/portals")} data-testid="portal-manage-link" className="text-[11px] font-semibold uppercase tracking-[0.15em] text-indigo-600 hover:text-indigo-800">Manage →</button>
                    </div>
                </div>
                {portalBal?.portals?.length ? (
                    <>
                        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 xl:grid-cols-4">
                            {portalBal.portals.map((p) => (
                                <div key={p.id} data-testid={`portal-card-${p.id}`} onClick={() => navigate(`/admin/portals/${p.id}/statement`)} className="cursor-pointer rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 transition-all hover:-translate-y-0.5 hover:border-indigo-300 hover:shadow-md">
                                    <div className="truncate text-[11px] font-semibold uppercase tracking-[0.15em] text-slate-500">{p.name}</div>
                                    <div data-testid={`portal-balance-${p.id}`} className={`mt-1.5 font-display text-xl font-bold tabular-nums ${p.balance < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmt(p.balance)}</div>
                                </div>
                            ))}
                        </div>
                        <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
                            <span className="text-sm font-semibold text-slate-700">All portals combined</span>
                            <span data-testid="portal-combined-total" className={`font-display text-xl font-bold tabular-nums ${(portalBal.combined || 0) < 0 ? "text-rose-600" : "text-emerald-700"}`}>{fmt(portalBal.combined)}</span>
                        </div>
                    </>
                ) : (
                    <p className="text-sm text-slate-500" data-testid="portal-balances-empty">No portals yet. <button onClick={() => navigate("/admin/portals")} className="font-semibold text-indigo-600 hover:underline">Add portals</button> and map their UPI accounts + withdrawal modes to see balances here.</p>
                )}
            </div>
            {/* v1.28.65 — Cash In Hand tile temporarily hidden per user request.
                Re-enable by changing the guard below to `true`. */}
            {false && (
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                    <CashInHandTile />
                </div>
            )}
            <MonthlySlipSummary refreshKey={data?.pending_uploads ?? 0} />
                    <Dialog open={payoutOpen} onOpenChange={setPayoutOpen}>
                    {/* v1.25.78 — Cap dialog height + scroll internally so the
                        Save button in DialogFooter is always visible on mobile.
                        Previously the party chip + cash counter + entries +
                        payment-mode manager stacked past the viewport and the
                        admin had to scroll the browser to find "Save". */}
                    {/* v1.28.01 — Wider + taller dialog on desktop, header/footer
                        pinned, entry list scrolls independently. */}
                    <DialogContent className="flex flex-col sm:max-w-[80vw] sm:h-[85vh] max-h-[90vh] p-0 gap-0">
                        <DialogHeader className="border-b border-slate-100 px-6 py-4">
                            <DialogTitle className="font-display text-xl">Amt. Paid / Value Out</DialogTitle>
                            <DialogDescription className="text-sm text-slate-500">
                                Pick the party once, then add every payment you've made. Each entry becomes its own transaction on their ledger, sorted by date.
                            </DialogDescription>
                        </DialogHeader>
                        <form onSubmit={submitPayouts} className="flex flex-1 flex-col min-h-0">
                          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4 text-base">
                            {/* v1.24.12 — Party chosen ONCE at the top of the batch */}
                            {/* v1.29.80 — Redesigned top: party on the left, 2x2 info tiles
                                (dark navy + green accent, white text) on the right, then a
                                full-width batch-total summary tile. Layout only — the state,
                                handlers, data and API calls are all unchanged. */}
                            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                                    <div className="sm:w-60 sm:shrink-0">
                                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Party *</Label>
                                        <Select value={payoutPartyId} onValueChange={setPayoutPartyId}>
                                            <SelectTrigger data-testid="payout-party" className="mt-1 bg-white">
                                                <SelectValue placeholder={sortedParties.length ? "Select party" : "No parties available"} />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-72">
                                                {sortedParties.map((p) => (
                                                    <SelectItem key={p.id} value={p.id}>
                                                        {p.name}{p.active === false ? " (inactive)" : ""}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {(() => {
                                        const bal = payoutPartyId ? partyBalances[payoutPartyId] : undefined;
                                        const newBal = bal === undefined ? undefined : bal + total; // payout is a debit (+)
                                        const balText = (v) => v === undefined ? "—" : `${fmt2(Math.abs(v))} ${v > 0 ? "receivable" : v < 0 ? "payable" : "settled"}`;
                                        return (
                                            <div className="grid flex-1 grid-cols-2 gap-2">
                                                <div className="rounded-md bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-300">Making Payments of</div>
                                                <div className="rounded-md bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-300">New balance</div>
                                                <div data-testid="payout-paying-amount" className="rounded-md bg-slate-900 px-3 py-2 font-display text-lg font-bold tabular-nums text-emerald-400">{fmt2(total)}</div>
                                                <div data-testid="payout-new-balance" className="rounded-md bg-slate-900 px-3 py-2 font-display text-base font-bold tabular-nums text-white">{balText(newBal)}</div>
                                            </div>
                                        );
                                    })()}
                                </div>
                                <div className="flex items-center justify-between rounded-md bg-emerald-600 px-4 py-2.5 text-white">
                                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-50">Batch total amount</span>
                                    <span data-testid="payout-total" className="font-display text-xl font-bold tabular-nums">{fmt2(total)}</span>
                                </div>
                            </div>

                            {/* v1.25.76 — Cash denomination counter: enter counts of each
                                Indian note/coin to reconcile physical cash against the
                                batch total before saving the payout.
                                v1.25.78 — When there is exactly ONE payment line, the
                                counter's total is auto-mirrored into that line's Amount
                                field (via onTotalChange) so admins don't have to retype
                                the number they just calculated. */}
                            <CashDenominationCounter
                                targetAmount={total}
                                testId="payout-cash-index"
                                onTotalChange={(t) => {
                                    if (t > 0 && lines.length === 1) {
                                        updateLine(lines[0].id, { amount: String(t) });
                                    }
                                }}
                            />
                            {lines.length > 1 && (
                                <div className="-mt-2 text-[11px] text-slate-500" data-testid="payout-cash-index-hint">
                                    Multiple entries active — cash counter total won't auto-fill any Amount. Split the total yourself.
                                </div>
                            )}

                            <div className="max-h-96 space-y-3 overflow-y-auto pr-1" data-testid="payout-lines">
                                {lines.map((l, idx) => (
                                    <div
                                        key={l.id}
                                        data-testid={`payout-line-${idx}`}
                                        className="grid grid-cols-12 items-start gap-2 rounded-md border border-slate-200 bg-white p-3"
                                    >
                                        <div className="col-span-6 sm:col-span-3">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Date *</Label>
                                            <Input
                                                data-testid={`payout-date-${idx}`}
                                                required
                                                type="date"
                                                value={l.date}
                                                onChange={(e) => updateLine(l.id, { date: e.target.value })}
                                                className="mt-1"
                                            />
                                        </div>
                                        <div className="col-span-6 sm:col-span-3">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Amount ₹ *</Label>
                                            <AmountInput
                                                data-testid={`payout-amount-${idx}`}
                                                variant="inline"
                                                value={l.amount}
                                                onChange={(e) => updateLine(l.id, { amount: e.target.value })}
                                                className="mt-1"
                                                placeholder="0"
                                                wordsStyle={{ fontSize: '14px', fontWeight: '700', color: '#111827', background: '#ffffff', padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: '4px', display: 'block', opacity: 1, visibility: 'visible' }}
                                            />
                                        </div>
                                        <div className="col-span-8 sm:col-span-3">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Payment Mode</Label>
                                            <Select value={l.payment_mode} onValueChange={(v) => updateLine(l.id, { payment_mode: v })}>
                                                <SelectTrigger data-testid={`payout-mode-${idx}`} className="mt-1"><SelectValue placeholder="Select mode" /></SelectTrigger>
                                                <SelectContent>
                                                    {sortedPaymentModes.map((m) => (
                                                        <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {/* v1.29.34 — wrong-direction mode on the Value Out form: instant red error */}
                                        {RECD_MODE_RE.test(l.payment_mode || "") && (
                                            <div data-testid={`payout-mode-error-${idx}`} role="alert" className="col-span-12 -mt-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-600">
                                                This payment mode is for Value Received. Please use the Value Received / Amt Recd form instead.
                                            </div>
                                        )}
                                        <div className="col-span-4 sm:col-span-2">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Remarks</Label>
                                            <Input
                                                data-testid={`payout-remarks-${idx}`}
                                                value={l.remarks}
                                                onChange={(e) => updateLine(l.id, { remarks: e.target.value })}
                                                className="mt-1"
                                                placeholder="Optional"
                                            />
                                        </div>
                                        <div className="col-span-12 sm:col-span-1 flex items-end justify-end">
                                            <button
                                                type="button"
                                                data-testid={`remove-payout-line-${idx}`}
                                                onClick={() => removeLine(l.id)}
                                                disabled={lines.length === 1}
                                                className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-40 disabled:hover:bg-transparent"
                                                title="Remove entry"
                                            >
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                data-testid="add-payout-line-btn"
                                onClick={addLine}
                                className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 py-3 text-sm text-slate-600 transition-colors hover:border-violet-400 hover:bg-violet-50/40 hover:text-slate-900"
                            >
                                <Plus size={16} /> Add another entry
                            </button>

                            {/* v1.25.9 — Add / manage payment modes inline. Newly-added modes
                                immediately appear in every row's dropdown and persist in the DB. */}
                            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="payment-modes-manager">
                                <div className="mb-2 flex items-center justify-between">
                                    <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Payment Modes ({paymentModes.length})</Label>
                                    <span className="text-[10px] text-slate-400">Used in the dropdown above</span>
                                </div>
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                    {sortedPaymentModes.map((m) => (
                                        <span key={m.id || m.name} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200">
                                            {m.name}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input
                                        data-testid="new-payment-mode-input"
                                        value={newModeName}
                                        onChange={(e) => setNewModeName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMode(); } }}
                                        placeholder="e.g. NEFT, Card, IMPS…"
                                        className="h-8 bg-white text-sm"
                                    />
                                    <Button
                                        type="button"
                                        data-testid="add-payment-mode-btn"
                                        onClick={addMode}
                                        disabled={!newModeName.trim()}
                                        variant="outline"
                                        className="h-8 shrink-0 border-violet-300 bg-white text-violet-700 hover:bg-violet-50"
                                    >
                                        <Plus size={13} className="mr-1" /> Add mode
                                    </Button>
                                </div>
                            </div>

                          </div>
                          {payoutError && (
                                <div data-testid="payout-error-inline" role="alert" className="mx-6 mb-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{payoutError}</div>
                          )}
                          {/* v1.28.01 — Pinned footer for payout dialog. */}
                          <DialogFooter className="border-t border-slate-100 bg-white px-6 py-3">
                                <Button type="button" variant="outline" onClick={() => setPayoutOpen(false)}>Cancel</Button>
                                <Button
                                    type="submit"
                                    data-testid="save-payouts-btn"
                                    disabled={busy || !payoutPartyId || validLines.length === 0 || recdModeOnPayout}
                                    className="bg-violet-600 text-white hover:bg-violet-700"
                                >
                                    {busy ? "Saving…" : `Record ${validLines.length || 0} payment${validLines.length === 1 ? "" : "s"}`}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

                {/* Record Payment Received dialog — v1.27.7: payment_mode is now user-selectable per line (was hardcoded Cash) */}
                <Dialog open={cashOpen} onOpenChange={setCashOpen}>
                    {/* v1.28.01 — Wider + taller Record Payment Received dialog. */}
                    <DialogContent data-testid="cash-dialog" className="flex flex-col sm:max-w-[80vw] sm:h-[85vh] max-h-[90vh] p-0 gap-0">
                        <DialogHeader className="border-b border-slate-100 px-6 py-4">
                            <DialogTitle className="font-display text-xl flex items-center gap-2">
                                <CurrencyInr size={22} weight="fill" className="text-emerald-600" /> Cash / Value Recd
                            </DialogTitle>
                            <DialogDescription className="text-xs text-slate-500">
                                Party paid you directly (no slip/UTR to upload). Pick the Payment Mode per line — Cash, UPI, NEFT, or any portal name you've added below.
                            </DialogDescription>
                            <div className="mt-2 flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-[11px] font-medium text-slate-600">
                                <span data-testid="cash-parties-count">
                                    <span className="font-semibold text-emerald-700">{sortedParties.length}</span> {sortedParties.length === 1 ? "party" : "parties"} available in the dropdown
                                </span>
                                <button
                                    type="button"
                                    onClick={() => api.get("/parties").then((r) => { setParties(Array.isArray(r.data) ? r.data : []); toast.success("Party list refreshed"); }).catch((e) => toast.error(formatApiError(e)))}
                                    className="text-blue-600 hover:text-blue-800"
                                >Refresh ↻</button>
                            </div>
                        </DialogHeader>
                        <form onSubmit={submitCash} className="flex flex-1 flex-col min-h-0">
                          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-3 text-base">
                            {/* v1.29.80 — Redesigned top (layout only): Date on the left,
                                navy/green/white info tiles + full-width green batch-total tile.
                                Kept multi-party — each entry row still has its own Party dropdown. */}
                            <div className="space-y-2 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                                <div className="flex flex-col gap-3 sm:flex-row sm:items-stretch">
                                    <div className="sm:w-60 sm:shrink-0">
                                        <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">Date *</Label>
                                        <Input data-testid="cash-date" required type="date" value={cashDate} onChange={(e) => setCashDate(e.target.value)} className="mt-1 bg-white" />
                                    </div>
                                    <div className="grid flex-1 grid-cols-2 gap-2">
                                        <div className="rounded-md bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-300">Total received</div>
                                        <div className="rounded-md bg-slate-900 px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-slate-300">Entries ready</div>
                                        <div data-testid="cash-received-amount" className="rounded-md bg-slate-900 px-3 py-2 font-display text-lg font-bold tabular-nums text-emerald-400">{fmt2(cashTotal)}</div>
                                        <div data-testid="cash-entries-ready" className="rounded-md bg-slate-900 px-3 py-2 font-display text-base font-bold tabular-nums text-white">{validCashLines.length} of {cashLines.length}</div>
                                    </div>
                                </div>
                                <div className="flex items-center justify-between rounded-md bg-emerald-600 px-4 py-2.5 text-white">
                                    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-50">Batch total amount</span>
                                    <span data-testid="cash-total" className="font-display text-xl font-bold tabular-nums">{fmt2(cashTotal)}</span>
                                </div>
                            </div>
                            <div className="max-h-96 space-y-3 overflow-y-auto pr-1">
                                {cashLines.map((l, idx) => (
                                    <div key={l.id} data-testid={`cash-line-${idx}`} className="grid grid-cols-12 items-start gap-2 rounded-md border border-slate-200 bg-white p-3">
                                        <div className="col-span-12 sm:col-span-4">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Party *</Label>
                                            <Select value={l.party_id} onValueChange={(v) => onCashPartySelected(l.id, v)}>
                                                <SelectTrigger data-testid={`cash-party-${idx}`} className="mt-1"><SelectValue placeholder={sortedParties.length ? "Select party" : "No parties — add one first"} /></SelectTrigger>
                                                <SelectContent className="max-h-72">
                                                    {sortedParties.length === 0 && (
                                                        <div className="px-3 py-4 text-center text-xs text-slate-500">No parties loaded. Click Refresh above.</div>
                                                    )}
                                                    {sortedParties.map((p) => (
                                                        <SelectItem key={p.id} value={p.id}>
                                                            {p.name}{p.active === false ? " (inactive)" : ""}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Amount ₹ *</Label>
                                            <AmountInput data-testid={`cash-amount-${idx}`} variant="inline" value={l.amount}
                                                onChange={(e) => setCashLineField(l.id, "amount", e.target.value)}
                                                className="mt-1" placeholder="0"
                                                wordsStyle={{ fontSize: '14px', fontWeight: '700', color: '#111827', background: '#ffffff', padding: '4px 10px', border: '1px solid #e5e7eb', borderRadius: '4px', display: 'block', opacity: 1, visibility: 'visible' }} />
                                        </div>
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Payment Mode</Label>
                                            <Select value={l.payment_mode || "Cash"} onValueChange={(v) => setCashLineField(l.id, "payment_mode", v)}>
                                                <SelectTrigger data-testid={`cash-mode-${idx}`} className="mt-1"><SelectValue placeholder="Cash" /></SelectTrigger>
                                                <SelectContent>
                                                    {paymentModes.length === 0 && (
                                                        <SelectItem value="Cash">Cash</SelectItem>
                                                    )}
                                                    {sortedPaymentModes.map((m) => (
                                                        <SelectItem key={m.id || m.name} value={m.name}>{m.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                        {/* v1.29.34 — wrong-direction mode on the Value In form: instant red error */}
                                        {PAID_MODE_RE.test(l.payment_mode || "") && (
                                            <div data-testid={`cash-mode-error-${idx}`} role="alert" className="col-span-12 -mt-1 rounded-md border border-red-200 bg-red-50 px-2.5 py-1.5 text-[11px] font-semibold text-red-600">
                                                This payment mode is for Value Paid / Amt Out. Please use the Amt Paid / Value Out form instead.
                                            </div>
                                        )}
                                        <div className="col-span-6 sm:col-span-2">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Charge %</Label>
                                            <Input
                                                data-testid={`cash-charge-${idx}`}
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={l.charge_percent}
                                                onChange={(e) => setCashLineField(l.id, "charge_percent", e.target.value)}
                                                placeholder="No commission"
                                                className="mt-1"
                                                title="Leave blank or 0 for NO commission. Click the party default below to apply the pricing-rule rate."
                                            />
                                            {Number(l.charge_percent || 0) > 0 && Number(l.amount || 0) > 0 && (
                                                <div className="mt-0.5 flex items-center justify-between gap-1 text-[10px]" data-testid={`cash-charge-preview-${idx}`}>
                                                    <span className="italic text-emerald-700">Commission {fmt2(Number(l.amount) * Number(l.charge_percent) / 100)}</span>
                                                    <button
                                                        type="button"
                                                        data-testid={`cash-clear-charge-${idx}`}
                                                        onClick={() => setCashLineField(l.id, "charge_percent", "")}
                                                        className="rounded px-1 text-[10px] font-semibold uppercase tracking-wider text-rose-600 hover:bg-rose-50"
                                                    >Clear</button>
                                                </div>
                                            )}
                                            {(!l.charge_percent || Number(l.charge_percent) === 0) && l.party_id && (
                                                partyRulePct[l.party_id] > 0 ? (
                                                    <button
                                                        type="button"
                                                        data-testid={`cash-apply-default-${idx}`}
                                                        onClick={() => setCashLineField(l.id, "charge_percent", String(partyRulePct[l.party_id]))}
                                                        className="mt-0.5 block truncate text-[10px] font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                                        title="Apply this party's pricing-rule rate"
                                                    >
                                                        Party default: {partyRulePct[l.party_id]}% · Apply →
                                                    </button>
                                                ) : (
                                                    <div className="mt-0.5 truncate text-[10px] italic text-slate-400">No commission</div>
                                                )
                                            )}
                                        </div>
                                        <div className="col-span-12 sm:col-span-3">
                                            <Label className="text-xs uppercase tracking-[0.15em] text-slate-500">Remarks</Label>
                                            <Input data-testid={`cash-remarks-${idx}`} value={l.remarks}
                                                onChange={(e) => setCashLineField(l.id, "remarks", e.target.value)}
                                                className="mt-1" placeholder="Optional note" />
                                        </div>
                                        <div className="col-span-12 sm:col-span-1 flex items-end justify-end">
                                            <button type="button" onClick={() => setCashLines((p) => p.length > 1 ? p.filter((x) => x.id !== l.id) : p)}
                                                disabled={cashLines.length === 1}
                                                className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-rose-600 disabled:opacity-40">
                                                <Trash size={16} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <button type="button" data-testid="add-cash-line-btn"
                                onClick={() => setCashLines((p) => [...p, { id: crypto.randomUUID(), party_id: "", amount: "", charge_percent: "", remarks: "", payment_mode: "Cash" }])}
                                className="flex w-full items-center justify-center gap-2 rounded-md border-2 border-dashed border-slate-300 bg-slate-50 py-3 text-sm text-slate-600 hover:border-emerald-400 hover:bg-emerald-50/40">
                                <Plus size={16} /> Add another entry
                            </button>
                            {cashCommissionTotal > 0 && (
                                <div className="flex items-center justify-between rounded-md border border-emerald-200 bg-emerald-50/40 px-4 py-2 text-xs text-emerald-800" data-testid="cash-commission-summary">
                                    <span>Total commission to be posted</span>
                                    <span className="font-mono font-semibold">{fmt2(cashCommissionTotal)}</span>
                                </div>
                            )}

                            {/* v1.27.7 — Manage payment modes inline. Newly-added modes immediately
                                appear in every row's Payment Mode dropdown and persist in the DB. */}
                            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3" data-testid="cash-payment-modes-manager">
                                <div className="mb-2 flex items-center justify-between">
                                    <Label className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Payment Modes ({paymentModes.length})</Label>
                                    <span className="text-[10px] text-slate-400">Add UPI / portal names once — reuse forever</span>
                                </div>
                                <div className="mb-2 flex flex-wrap gap-1.5">
                                    {sortedPaymentModes.map((m) => (
                                        <span key={m.id || m.name} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[11px] text-slate-700 ring-1 ring-slate-200">
                                            {m.name}
                                        </span>
                                    ))}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Input
                                        data-testid="cash-new-payment-mode-input"
                                        value={newModeName}
                                        onChange={(e) => setNewModeName(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addMode(); } }}
                                        placeholder="e.g. PhonePe, Amazon Pay, Distributor A…"
                                        className="h-8 bg-white text-sm"
                                    />
                                    <Button
                                        type="button"
                                        data-testid="cash-add-payment-mode-btn"
                                        onClick={addMode}
                                        disabled={!newModeName.trim()}
                                        variant="outline"
                                        className="h-8 shrink-0 border-emerald-300 bg-white text-emerald-700 hover:bg-emerald-50"
                                    >
                                        <Plus size={13} className="mr-1" /> Add mode
                                    </Button>
                                </div>
                            </div>

                          </div>
                          {cashError && (
                                <div data-testid="cash-error-inline" role="alert" className="mx-6 mb-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">{cashError}</div>
                          )}
                          {/* v1.28.01 — Pinned footer so Cancel + Record buttons
                              stay visible no matter how many entries are added. */}
                          <DialogFooter className="border-t border-slate-100 bg-white px-6 py-3">
                                <Button type="button" variant="outline" onClick={() => setCashOpen(false)}>Cancel</Button>
                                <Button type="submit" data-testid="save-cash-btn" disabled={cashBusy || validCashLines.length === 0 || paidModeOnCash} className="bg-emerald-600 text-white hover:bg-emerald-700">
                                    {cashBusy ? "Saving…" : `Record ${validCashLines.length || 0} payment${validCashLines.length === 1 ? "" : "s"}`}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>

            <div className="grid grid-cols-5 gap-2">
                {stats.map((s) => (
                    <button
                        type="button"
                        key={s.label}
                        onClick={s.onClick}
                        data-testid={`stat-${s.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white px-2 py-3 text-left shadow-sm transition-all sm:p-3 duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
                    >
                        <div aria-hidden className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br opacity-10 blur-2xl transition-opacity group-hover:opacity-20 ${s.accent}`} />
                        <div className="flex items-center justify-between gap-2">
                            <div className="text-[9px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:text-[10px]">{s.label}</div>
                            <div className={`hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br sm:flex text-white shadow-sm ${s.accent}`}>
                                <s.icon size={18} weight="bold" />
                            </div>
                        </div>
                        <div
                            title={s.sub || String(s.value)}
                            className={`mt-1.5 font-display text-[10px] font-bold tabular-nums leading-none tracking-tight sm:text-xl sm:text-xl whitespace-nowrap ${s.tone}`}
                        >
                            {s.raw != null && s.format ? <CountUp to={s.raw} format={s.format} /> : s.value}
                        </div>
                        {s.sub && (
                            <div className="mt-1 hidden truncate text-[11px] font-medium text-slate-400 sm:block sm:text-xs">{s.sub}</div>
                        )}
                        {s.hint && (
                            <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-600 opacity-0 transition-opacity group-hover:opacity-100 sm:text-[11px]">
                                {s.hint} <ArrowRight size={12} weight="bold" />
                            </div>
                        )}
                    </button>
                ))}

                {/* v1.27.80 — Pending Uploads + Missing UTR tiles removed from
                    Dashboard. Both metrics moved into the dedicated
                    /admin/reports/slip-summary page. */}

                {/* Task 5 — Commission earned in the last 3 calendar months */}
                <button
                    type="button"
                    onClick={() => navigate("/admin/reports/commission")}
                    data-testid="stat-commission-3m"
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white px-2 py-3 text-left shadow-sm transition-all sm:p-3 duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400"
                >
                    <div aria-hidden className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 opacity-10 blur-2xl" />
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:text-[10px]">Commission · Last 3 Months</div>
                        <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br sm:flex from-emerald-500 to-teal-600 text-white shadow-sm">
                            <ChartLineUp size={18} weight="bold" />
                        </div>
                    </div>
                    <div
                        title={fmt(data?.commission_3m_total || 0)}
                        className="mt-1.5 font-display text-[10px] font-bold tabular-nums leading-none tracking-tight sm:text-xl text-emerald-800 sm:text-xl whitespace-nowrap"
                        data-testid="commission-3m-total"
                    >
                        {fmtCompact(data?.commission_3m_total)}
                    </div>
                    <div className="mt-1 hidden truncate text-[11px] font-medium text-slate-400 sm:block sm:text-xs">Last 3 months combined · tap for full chart</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-emerald-700 opacity-0 transition-opacity group-hover:opacity-100 sm:text-[11px]">
                        View report <ArrowRight size={12} weight="bold" />
                    </div>
                </button>
                {/* v1.31.2 — Mediator commission earned this month across all providers (live estimate). */}
                <button
                    type="button"
                    onClick={() => navigate("/admin/reports/mediator-commission")}
                    data-testid="stat-mediator-commission"
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white px-2 py-3 text-left shadow-sm transition-all sm:p-3 duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-400"
                >
                    <div aria-hidden className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-teal-500 to-emerald-600 opacity-10 blur-2xl" />
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:text-[10px]">Mediator Commission · {medComm?.month_label || "This Month"}</div>
                        <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br sm:flex from-teal-500 to-emerald-600 text-white shadow-sm">
                            <CurrencyInr size={18} weight="bold" />
                        </div>
                    </div>
                    <div
                        title={fmt(medComm?.total || 0)}
                        className="mt-1.5 font-display text-[10px] font-bold tabular-nums leading-none tracking-tight sm:text-xl text-teal-800 sm:text-xl whitespace-nowrap"
                        data-testid="mediator-commission-total"
                    >
                        {fmtCompact(medComm?.total || 0)}
                    </div>
                    <div className="mt-1 hidden truncate text-[11px] font-medium text-slate-400 sm:block sm:text-xs">{medComm?.count || 0} mediator{(medComm?.count || 0) === 1 ? "" : "s"} · live estimate</div>
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-teal-600 opacity-0 transition-opacity group-hover:opacity-100 sm:text-[11px]">
                        View report <ArrowRight size={12} weight="bold" />
                    </div>
                </button>
                {/* v1.21.0 — Missing UTR tile removed in v1.27.80 — metric now
                    lives on /admin/reports/slip-summary. */}
                {/* v1.25.2 — Big Payable Balance tile (was a full-width banner above the welcome). Click to expand the party list inline below. */}
                <button
                    type="button"
                    onClick={() => setShowAdvancePanel((v) => !v)}
                    data-testid="stat-advance-alerts"
                    className="group relative cursor-pointer overflow-hidden rounded-xl border border-slate-200 bg-white px-2 py-3 text-left shadow-sm transition-all sm:p-3 duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
                >
                    <div aria-hidden className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 opacity-10 blur-2xl" />
                    <div className="flex items-center justify-between gap-2">
                        <div className="text-[9px] font-medium uppercase tracking-[0.18em] text-slate-500 sm:text-[10px]">Big Payable Balance</div>
                        <div className="hidden h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br sm:flex from-amber-400 to-orange-500 text-white shadow-sm">
                            <Warning size={18} weight="bold" />
                        </div>
                    </div>
                    <div className="mt-1.5 font-display text-[10px] font-bold tabular-nums leading-none tracking-tight sm:text-xl text-amber-800 sm:text-xl whitespace-nowrap" data-testid="advance-alerts-count">
                        <CountUp to={(data?.advance_alerts || []).length} format={(n) => Math.round(n).toString()} />
                    </div>
                    <div className="mt-1.5 hidden text-[11px] font-medium text-slate-400 sm:block sm:text-xs">
                        parties · advance ₹{fmt2((data?.advance_alerts || []).reduce((s, r) => s + Math.abs(r.balance || 0), 0)).replace("₹","")}
                    </div>
                    <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-amber-700 opacity-0 transition-opacity group-hover:opacity-100 sm:text-[11px]">
                        {showAdvancePanel ? "Hide list" : "Show list"} <ArrowRight size={12} weight="bold" />
                    </div>
                </button>
            </div>

            {/* Big Payable Balance — top 3 preview + link to the full report
                (the full list is intentionally NOT shown on the dashboard). */}
            {showAdvancePanel && (
                <div data-testid="big-payable-preview" className="rounded-xl border border-amber-300 bg-amber-50/60 p-4 shadow-sm">
                    <div className="mb-2 flex items-center justify-between">
                        <h3 className="font-display text-sm font-bold text-amber-900">Top parties by payable balance</h3>
                        <button type="button" data-testid="big-payable-view-full" onClick={() => navigate("/admin/reports/outstanding")} className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.15em] text-amber-800 hover:text-amber-900">
                            View Full Report <ArrowRight size={12} weight="bold" />
                        </button>
                    </div>
                    <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-3">
                        {[...(data?.advance_alerts || [])]
                            .sort((a, b) => Math.abs(b.balance ?? b.advance ?? 0) - Math.abs(a.balance ?? a.advance ?? 0))
                            .slice(0, 3)
                            .map((r) => {
                                const pid = r.party_id || r.id;
                                const amt = Math.abs(r.balance ?? r.advance ?? 0);
                                return (
                                    <button key={pid} type="button" data-testid={`big-payable-row-${pid}`} onClick={() => navigate(`/admin/reports?partyId=${pid}`)} className="flex items-center justify-between gap-2 rounded-md border border-amber-200 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:border-amber-400 hover:bg-amber-50">
                                        <span className="truncate font-semibold text-slate-900">{r.name || r.party_name || "Party"}</span>
                                        <span className="shrink-0 font-mono text-sm font-bold text-rose-700">-{fmt(amt)}</span>
                                    </button>
                                );
                            })}
                    </div>
                </div>
            )}

            {/* v1.21.0 — Quick-nav to reconcile/reporting toolkits */}
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <QuickNavCard testId="nav-leaderboard" title="Leaderboard" desc="Highest earning parties" icon={Trophy} tone="from-amber-400 to-orange-500" onClick={() => navigate("/admin/reports/leaderboard")} />
                <QuickNavCard testId="nav-stranded" title="Stranded commissions" desc="Retro-heal ledger gaps" icon={Broom} tone="from-rose-500 to-red-600" onClick={() => navigate("/admin/reports/stranded")} />
                <QuickNavCard testId="nav-grid" title="Reconcile grid" desc="Party × month status" icon={TableIcon} tone="from-blue-500 to-indigo-600" onClick={() => navigate("/admin/reports/reconcile-grid")} />
                <QuickNavCard testId="nav-search" title="Global search" desc="UTR · ref · amount" icon={UsersThree} tone="from-slate-600 to-slate-800" onClick={() => navigate("/admin/search")} />
                <QuickNavCard testId="nav-opening-closing" title="Opening & Closing" desc="Monthly balances per party" icon={Scales} tone="from-teal-500 to-emerald-600" onClick={() => navigate("/admin/reports/opening-closing")} />
            </div>

            {/* v1.31.16 — Top Parties of the Month + Recent Payment Slips as two
                aligned, compact half-width tiles (stack on mobile). */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 lg:items-start">
                <TopPartiesWidget />

                <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-100 px-3 py-2">
                        <h2 className="font-display text-sm font-bold text-slate-900">Recent Payment Slips</h2>
                        <a href="/admin/uploads" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.15em] text-blue-600 hover:text-blue-700">
                            View all <ArrowRight size={12} weight="bold" />
                        </a>
                    </div>
                    {(data?.recent_uploads || []).length === 0 ? (
                        <div className="px-3 py-6 text-center text-xs text-slate-400">No uploads yet.</div>
                    ) : (
                        <ul className="divide-y divide-slate-100">
                            {(data?.recent_uploads || []).slice(0, 5).map((u) => (
                                <li key={u.id} className="flex items-center gap-2 px-3 py-1.5">
                                    <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-semibold text-slate-800" data-testid={`dash-recent-party-${u.id}`}>{u.party_name || "—"}</div>
                                        <div className="truncate text-[10px] text-slate-400">
                                            {(() => { const d = new Date(u.uploaded_at); const p = (n) => String(n).padStart(2, "0"); return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`; })()}{u.utr ? ` · ${u.utr}` : ""}
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <div className="text-sm font-bold tabular-nums text-slate-900">{fmt(u.amount)}</div>
                                        <div className="mt-0.5"><StatusBadge status={u.status} /></div>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>

            {/* Outstanding drill-down modal */}
            <Dialog open={outstandingOpen} onOpenChange={setOutstandingOpen}>
                <DialogContent data-testid="outstanding-dialog" className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle className="font-display flex items-center gap-2">
                            <CurrencyInr size={22} weight="fill" className="text-yellow-600" />
                            Outstanding by Party
                        </DialogTitle>
                        <DialogDescription className="text-xs text-slate-500">
                            Total: <span className="font-mono font-semibold text-slate-900">{fmt(data?.total_outstanding)}</span> across {outstandingRows?.length ?? "…"} parties. Click a row to open that party's transactions.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[65vh] overflow-y-auto rounded-lg border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="sticky top-0 bg-slate-900 text-xs uppercase tracking-wider text-white">
                                <tr>
                                    <th className="px-4 py-2 text-left font-medium">Party</th>
                                    <th className="px-4 py-2 text-right font-medium">Opening</th>
                                    <th className="px-4 py-2 text-right font-medium">Outstanding</th>
                                    <th className="px-4 py-2 text-right font-medium">Txns</th>
                                    <th className="px-4 py-2 font-medium" />
                                </tr>
                            </thead>
                            <tbody data-testid="outstanding-rows">
                                {outstandingRows === null && (
                                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">Loading…</td></tr>
                                )}
                                {outstandingRows?.length === 0 && (
                                    <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">No parties yet.</td></tr>
                                )}
                                {outstandingRows?.map((r) => (
                                    <tr
                                        key={r.id}
                                        data-testid={`outstanding-row-${r.id}`}
                                        onClick={() => { setOutstandingOpen(false); navigate(`/admin/transactions?party_id=${r.id}`); }}
                                        className="cursor-pointer border-t border-slate-100 hover:bg-blue-50/40"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-semibold text-slate-900">{r.name}</div>
                                            {r.contact_person && <div className="text-xs text-slate-500">{r.contact_person}{r.phone ? ` · ${r.phone}` : ""}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-right font-mono tabular-nums text-slate-600">{fmt(r.opening_balance)}</td>
                                        <td className={`px-4 py-3 text-right font-mono tabular-nums font-semibold ${r.outstanding > 0 ? "text-emerald-700" : r.outstanding < 0 ? "text-rose-700" : "text-slate-600"}`}>{fmt(r.outstanding)}</td>
                                        <td className="px-4 py-3 text-right text-xs text-slate-500 tabular-nums">{r.txn_count}</td>
                                        <td className="px-4 py-3 text-right"><ArrowRight size={14} className="text-slate-400" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </DialogContent>
            </Dialog>

            {/* v1.26.0 — Duplicate payment guard warning dialog */}
            <Dialog open={!!dupWarning} onOpenChange={(o) => { if (!o) setDupWarning(null); }}>
                <DialogContent className="max-w-xl" data-testid="dup-warning-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-amber-700">
                            <Warning size={20} weight="fill" /> Possible duplicate payment
                        </DialogTitle>
                        <DialogDescription className="text-sm text-slate-600">
                            {dupWarning?.message || "A matching entry already exists on this party's ledger."}
                        </DialogDescription>
                    </DialogHeader>
                    {dupWarning?.existing && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm" data-testid="dup-existing">
                            <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-800">Already on the ledger</div>
                            <div className="mt-1 grid grid-cols-2 gap-x-4 gap-y-1 text-slate-800">
                                <div className="text-slate-500">Date</div><div className="text-right font-mono">{dmY(dupWarning.existing.date)}</div>
                                <div className="text-slate-500">Amount</div><div className="text-right font-mono font-semibold">₹{Number(dupWarning.existing.amount).toLocaleString("en-IN")}</div>
                                <div className="text-slate-500">Mode</div><div className="text-right">{dupWarning.existing.payment_mode || "—"}</div>
                                {dupWarning.existing.reference && (<>
                                    <div className="text-slate-500">Ref/UTR</div>
                                    <div className="text-right font-mono text-xs">{dupWarning.existing.reference}</div>
                                </>)}
                                <div className="text-slate-500">Posted at</div><div className="text-right text-xs text-slate-500">{dupWarning.existing.created_at ? dmYhm(dupWarning.existing.created_at) : "—"}</div>
                                <div className="text-slate-500">Posted by</div><div className="text-right text-xs text-slate-500">{dupWarning.existing.created_by || "—"}</div>
                            </div>
                        </div>
                    )}
                    {dupWarning?.duplicates && dupWarning.duplicates.length > 0 && (
                        <div className="max-h-64 space-y-2 overflow-y-auto rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs" data-testid="dup-list">
                            {dupWarning.duplicates.map((d, i) => {
                                // v1.29.44 — Payout duplicates come back as
                                // { party_id, date, amount, existing:{id,txn_type,reference} }
                                // (no `line`), cash as { line, existing }. Read both
                                // shapes defensively so rendering never throws — a bad
                                // access here used to crash AdminDashboard, trip the
                                // BootErrorBoundary and bounce the app to /login on the
                                // SECOND payment to the same party.
                                const pid = d.party_id || d.line?.party_id;
                                const triedDate = d.date || d.line?.date;
                                const triedAmt = d.amount ?? d.line?.amount;
                                const triedMode = d.line?.payment_mode;
                                const ex = d.existing || {};
                                const pName = parties.find((p) => p.id === pid)?.name || d.line?.party_name || pid || "This party";
                                return (
                                    <div key={i} className="rounded border border-amber-100 bg-white p-2" data-testid={`dup-item-${i}`}>
                                        <div className="font-semibold text-slate-800">{pName}</div>
                                        <div className="mt-0.5 flex justify-between text-slate-600">
                                            <span>Tried to post: {triedDate ? dmY(triedDate) : "—"} · ₹{Number(triedAmt || 0).toLocaleString("en-IN")}{triedMode ? ` · ${triedMode}` : ""}</span>
                                        </div>
                                        {(ex.date || ex.amount != null || ex.reference || ex.txn_type) && (
                                            <div className="mt-0.5 text-slate-500">
                                                Existing:{ex.date ? ` ${dmY(ex.date)}` : ""}{ex.amount != null ? ` · ₹${Number(ex.amount).toLocaleString("en-IN")}` : ""}{ex.txn_type ? ` · ${ex.txn_type}` : ""}{ex.reference ? ` · Ref ${ex.reference}` : ""}{ex.created_by ? ` · by ${ex.created_by}` : ""}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                    <DialogFooter className="gap-2 sm:justify-between">
                        <Button
                            type="button"
                            variant="outline"
                            data-testid="dup-reject-btn"
                            onClick={() => setDupWarning(null)}
                            className="border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                            Reject
                        </Button>
                        <Button
                            type="button"
                            data-testid="dup-post-anyway-btn"
                            disabled={dupBusy}
                            onClick={async () => {
                                if (!dupWarning) return;
                                setDupBusy(true);
                                try {
                                    if (dupWarning.kind === "payout") {
                                        // Re-post with override_duplicate flipped on
                                        const body = { ...dupWarning.pendingBody, override_duplicate: true };
                                        const { data } = await api.post("/payouts/bulk", body, { skipAuthRedirect: true });
                                        const partyName = sortedParties.find((p) => p.id === body.party_id)?.name || "party";
                                        toast.success(`Posted anyway · ${data.count} payment${data.count > 1 ? "s" : ""} to ${partyName} — total ${fmt2(data.total)}`);
                                        setPayoutOpen(false);
                                        setLines([blankLine()]);
                                        setPayoutPartyId("");
                                        setBurst(true);
                                        load();
                                        returnToSourceIfAny();
                                    } else if (dupWarning.kind === "cash") {
                                        // Retry the duplicated line first (with override), then the rest.
                                        // pendingLines starts with the duplicated line at index 0.
                                        const linesToRetry = dupWarning.pendingLines || [];
                                        for (const l of linesToRetry) {
                                            const pct = Number(l.charge_percent || 0);
                                            await api.post("/payments/cash", {
                                                party_id: l.party_id,
                                                date: cashDate,
                                                amount: Number(l.amount),
                                                charge_percent: pct > 0 ? pct : 0,
                                                remarks: l.remarks || "",
                                                payment_mode: l.payment_mode || "Cash",
                                                override_duplicate: true,
                                            }, { skipAuthRedirect: true });
                                        }
                                        toast.success(`Posted anyway · ${linesToRetry.length} payment${linesToRetry.length === 1 ? "" : "s"}`);
                                        setCashOpen(false);
                                        setCashLines([{ id: crypto.randomUUID(), party_id: "", amount: "", charge_percent: "", remarks: "", payment_mode: "Cash" }]);
                                        load();
                                        returnToSourceIfAny();
                                    }
                                    setDupWarning(null);
                                } catch (e) {
                                    toast.error(formatApiError(e));
                                } finally {
                                    setDupBusy(false);
                                }
                            }}
                            className="bg-amber-600 text-white hover:bg-amber-700"
                        >
                            {dupBusy ? "Posting…" : "Post Anyway"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function QuickNavCard({ title, desc, icon: Icon, tone, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white px-2 py-3 text-left shadow-sm transition-all sm:p-3 duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400"
        >
            <div aria-hidden className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${tone} opacity-10 blur-2xl`} />
            <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br ${tone} text-white shadow-sm`}>
                <Icon size={18} weight="bold" />
            </div>
            <div className="mt-2 font-display text-sm font-semibold text-slate-900">{title}</div>
            <div className="mt-0.5 text-[11px] text-slate-500">{desc}</div>
            <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-blue-600 opacity-0 transition-opacity group-hover:opacity-100">
                Open <ArrowRight size={12} weight="bold" />
            </div>
        </button>
    );
}
